import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTooltip } from '@/components/CustomTooltip/CustomTooltip';
import './FilterHeader.scss';

const BdsIcon = 'bds-icon' as any;
const BdsAutocomplete = 'bds-autocomplete' as any;


interface ChipsSectionProps {
  openSeaActive: boolean;
  proprietaryChannelsActive: boolean;
  hasChipError: boolean;
  onChipChange: (chip: 'openSea' | 'proprietaryChannels') => void;
  chipsAreLocked?: boolean;
  proprietaryChipDisabledReason?: string | null;
}

const ChipsSection: React.FC<ChipsSectionProps> = ({
  openSeaActive,
  proprietaryChannelsActive,
  hasChipError,
  onChipChange,
  chipsAreLocked = false,
  proprietaryChipDisabledReason = null,
}) => {
  const { t } = useTranslation();
  const proprietaryChipIsDisabled = chipsAreLocked || Boolean(proprietaryChipDisabledReason);
  const chipsInfoTooltipText = t(
    'globalFilter.chipsInfoTooltip',
    'Quando a opção Mar aberto estiver selecionada, significa que o filtro será aplicado apenas em postagens públicas em todos os canais, enquanto a opção Canais proprietários aplica o filtro em interações diretas nos perfis oficiais da sua empresa.',
  );

  const proprietaryChipButton = (
    <button
      type="button"
      className={`chip ${proprietaryChannelsActive ? 'chip--active' : 'chip--inactive'} ${hasChipError ? 'chip--error' : ''}`}
      onClick={() => onChipChange('proprietaryChannels')}
      aria-pressed={proprietaryChannelsActive}
      disabled={proprietaryChipIsDisabled}
      data-testid="chip-proprietary"
    >
      {proprietaryChannelsActive && <BdsIcon className="chip__icon" name="checkball" />}
      <span className="chip__text">{t('globalFilter.proprietaryChannels')}</span>
    </button>
  );

  return (
    <>
      <div className={`filter-header__chips-row ${hasChipError ? 'filter-header__chips-row--has-error' : ''}`}>
        <div className="filter-header__chips">
          <button
            type="button"
            className={`chip ${openSeaActive ? 'chip--active' : 'chip--inactive'} ${hasChipError ? 'chip--error' : ''}`}
            onClick={() => onChipChange('openSea')}
            aria-pressed={openSeaActive}
            disabled={chipsAreLocked}
          >
            {openSeaActive && <BdsIcon className="chip__icon" name="checkball" />}
            <span className="chip__text">{t('globalFilter.openSea')}</span>
          </button>
           {proprietaryChipDisabledReason ? (
            <CustomTooltip content={proprietaryChipDisabledReason} position="bottom" maxWidth="260px">
              {proprietaryChipButton}
            </CustomTooltip>
          ) : (
            proprietaryChipButton
          )}
          </div>

        <CustomTooltip
          content={chipsInfoTooltipText}
          position="bottom"
          maxWidth="220px"
          className="filter-header__chips-info-tooltip"
        >
          <span
            className="filter-header__chips-info-icon"
            data-testid="chips-info-tooltip-trigger"
          >
            <BdsIcon name="info" size="x-small" aria-hidden="true" />
          </span>
        </CustomTooltip>
      </div>
      {hasChipError && (
        <div className="filter-header__chips-error">
          <span className="filter-header__chips-error-icon"><BdsIcon name="error" /></span>
          <span className="filter-header__chips-error-text">{t('globalFilter.chipsErrorMessage')}</span>
        </div>
      )}
    </>
  );
};

interface SavedFilterOption {
  value: string;
  label: string;
}

type DropdownName = 'openSea' | 'proprietary';

type SelectionSource = 'bdsSelectedChange' | 'bdsCancel';

type SelectionSnapshot = Record<DropdownName, string>;

const EMPTY_SELECTION: SelectionSnapshot = { openSea: '', proprietary: '' };

function readSelectedValue(event: any, options: SavedFilterOption[]): string | null {
  const rawValue = event?.detail?.value;
  if (rawValue === undefined || rawValue === null || rawValue === '') return null;

  const value = String(rawValue);
  const isKnownOption = options.some((option) => option.value === value);
  return isKnownOption ? value : null;
}

interface AutocompleteBinding {
  name: DropdownName;
  getOptions: () => SavedFilterOption[];
  onSelect: (value: string, source: SelectionSource) => void;
}

function bindAutocompleteEvents(node: any, binding: AutocompleteBinding): (() => void) | undefined {
  if (!node) {
    return undefined;
  }

  const handleSelected = (event: any) => {
    const value = readSelectedValue(event, binding.getOptions());
    if (value === null) return;
    binding.onSelect(value, 'bdsSelectedChange');
  };

  const handleCancel = () => {
    binding.onSelect('', 'bdsCancel');
  };

  node.addEventListener('bdsSelectedChange', handleSelected);
  node.addEventListener('bdsCancel', handleCancel);

  return () => {
    node.removeEventListener('bdsSelectedChange', handleSelected);
    node.removeEventListener('bdsCancel', handleCancel);
  };
}

function setAutocompleteOptions(node: any, options: string): void {
  if (!node) return;
  node.options = options;
}

function syncAutocompleteValue(node: any, value: string): void {
  if (!node) return;

  if (!('value' in node)) return;

  const currentValue = node.value === undefined || node.value === null ? '' : String(node.value);
  if (currentValue === value) return;

  node.value = value;
}

function resolveExternalSelection(
  selectedFilter: string,
  openSeaFilters: SavedFilterOption[],
  proprietaryFilters: SavedFilterOption[],
): SelectionSnapshot | null {
  if (!selectedFilter) return EMPTY_SELECTION;
  if (openSeaFilters.some((filter) => filter.value === selectedFilter)) {
    return { openSea: selectedFilter, proprietary: '' };
  }
  if (proprietaryFilters.some((filter) => filter.value === selectedFilter)) {
    return { openSea: '', proprietary: selectedFilter };
  }
  return null;
}

interface SavedFilterSelectionArgs {
  openSeaFilters: SavedFilterOption[];
  proprietaryFilters: SavedFilterOption[];
  openSeaOptions: string;
  proprietaryOptions: string;
  selectedFilter: string;
  onFilterSelect?: (value: string) => void;
  openSeaNodeRef: React.RefObject<any>;
  proprietaryNodeRef: React.RefObject<any>;
  savedFiltersVisible: boolean;
}

function useSavedFilterSelection({
  openSeaFilters,
  proprietaryFilters,
  openSeaOptions,
  proprietaryOptions,
  selectedFilter,
  onFilterSelect,
  openSeaNodeRef,
  proprietaryNodeRef,
  savedFiltersVisible,
}: SavedFilterSelectionArgs) {
  const [openSeaValue, setOpenSeaValue] = useState('');
  const [proprietaryValue, setProprietaryValue] = useState('');
  const [lastChanged, setLastChanged] = useState<DropdownName | null>(null);
  const [lastEmittedFilter, setLastEmittedFilter] = useState(selectedFilter);
  const [prevSelectedFilter, setPrevSelectedFilter] = useState(selectedFilter);

  const selectionRef = useRef<SelectionSnapshot>(EMPTY_SELECTION);
  const optionsRef = useRef<Record<DropdownName, SavedFilterOption[]>>({ openSea: [], proprietary: [] });

  useEffect(() => {
    optionsRef.current = { openSea: openSeaFilters, proprietary: proprietaryFilters };
  }, [openSeaFilters, proprietaryFilters]);

  useEffect(() => {
    selectionRef.current = { openSea: openSeaValue, proprietary: proprietaryValue };
  }, [openSeaValue, proprietaryValue]);

  const applySelection = useCallback((next: SelectionSnapshot, changed: DropdownName) => {
    const hasBothDropdownsFilled = !!next.openSea && !!next.proprietary;
    const valueToEmit = hasBothDropdownsFilled ? '' : (next.openSea || next.proprietary);


    selectionRef.current = next;
    setOpenSeaValue(next.openSea);
    setProprietaryValue(next.proprietary);
    setLastChanged(changed);
    setLastEmittedFilter(valueToEmit);
    onFilterSelect?.(valueToEmit);
  }, [onFilterSelect]);

  const selectValue = useCallback((name: DropdownName, value: string, source: SelectionSource) => {
    const current = selectionRef.current;

    if (source === 'bdsSelectedChange' && value === current[name]) {
      return;
    }

    applySelection({ ...current, [name]: value }, name);
  }, [applySelection]);

  if (selectedFilter !== prevSelectedFilter) {
    setPrevSelectedFilter(selectedFilter);

    const isEchoOfOurOwnEmit = selectedFilter === lastEmittedFilter;
    const externalSelection = isEchoOfOurOwnEmit
      ? null
      : resolveExternalSelection(selectedFilter, openSeaFilters, proprietaryFilters);

    if (externalSelection) {
      setOpenSeaValue(externalSelection.openSea);
      setProprietaryValue(externalSelection.proprietary);
      setLastChanged(null);
      setLastEmittedFilter(selectedFilter);
    }
  }

  useEffect(() => bindAutocompleteEvents(openSeaNodeRef.current, {
    name: 'openSea',
    getOptions: () => optionsRef.current.openSea,
    onSelect: (value, source) => selectValue('openSea', value, source),
  }), [openSeaNodeRef, selectValue, savedFiltersVisible]);

  useEffect(() => bindAutocompleteEvents(proprietaryNodeRef.current, {
    name: 'proprietary',
    getOptions: () => optionsRef.current.proprietary,
    onSelect: (value, source) => selectValue('proprietary', value, source),
  }), [proprietaryNodeRef, selectValue, savedFiltersVisible]);

  useEffect(
    () => setAutocompleteOptions(openSeaNodeRef.current, openSeaOptions),
    [openSeaNodeRef, openSeaOptions, savedFiltersVisible],
  );
  useEffect(
    () => setAutocompleteOptions(proprietaryNodeRef.current, proprietaryOptions),
    [proprietaryNodeRef, proprietaryOptions, savedFiltersVisible],
  );

  useEffect(
    () => syncAutocompleteValue(openSeaNodeRef.current, openSeaValue),
    [openSeaNodeRef, openSeaValue, openSeaOptions, savedFiltersVisible],
  );
  useEffect(
    () => syncAutocompleteValue(proprietaryNodeRef.current, proprietaryValue),
    [proprietaryNodeRef, proprietaryValue, proprietaryOptions, savedFiltersVisible],
  );

  const hasBothSavedFiltersSelected = !!openSeaValue && !!proprietaryValue;

  return {
    openSeaValue,
    proprietaryValue,
    openSeaHasError: hasBothSavedFiltersSelected && lastChanged === 'openSea',
    proprietaryHasError: hasBothSavedFiltersSelected && lastChanged === 'proprietary',
  };
}

interface FilterHeaderProps {
  activeTab: 'newSearch' | 'savedFilters';
  onTabChange: (tab: 'newSearch' | 'savedFilters') => void;
  openSeaActive: boolean;
  proprietaryChannelsActive: boolean;
  onChipChange: (chip: 'openSea' | 'proprietaryChannels') => void;
  openSeaFilters?: SavedFilterOption[];
  proprietaryFilters?: SavedFilterOption[];
  selectedFilter?: string;
  onFilterSelect?: (value: string) => void;
  onClose: () => void;
  isLoadingFilters?: boolean;
  editMode?: boolean;
  editingFilterName?: string;
  onBack?: () => void;
  hideChipsRow?: boolean;
  chipsAreLocked?: boolean;
  proprietaryChipDisabledReason?: string | null;
  hideSavedFilters?: boolean;
}

export const FilterHeader: React.FC<FilterHeaderProps> = ({
  activeTab,
  onTabChange,
  openSeaActive = true,
  proprietaryChannelsActive = false,
  onChipChange,
  openSeaFilters = [],
  proprietaryFilters = [],
  selectedFilter = '',
  onFilterSelect,
  onClose,
  isLoadingFilters = false,
  editMode = false,
  editingFilterName = '',
  onBack,
  hideChipsRow = false,
  chipsAreLocked = false,
  proprietaryChipDisabledReason = null,
  hideSavedFilters = false,
}) => {
  const { t } = useTranslation();
  const openSeaAutocompleteRef = useRef<any>(null);
  const proprietaryAutocompleteRef = useRef<any>(null);

  const openSeaOptions = JSON.stringify(
    openSeaFilters.map((filter) => ({ value: filter.value, label: filter.label }))
  );
  const proprietaryOptions = JSON.stringify(
    proprietaryFilters.map((filter) => ({ value: filter.value, label: filter.label }))
  );

  const savedFiltersVisible = !hideSavedFilters && activeTab === 'savedFilters' && !editMode;

  const {
    openSeaValue,
    proprietaryValue,
    openSeaHasError,
    proprietaryHasError,
  } = useSavedFilterSelection({
    openSeaFilters,
    proprietaryFilters,
    openSeaOptions,
    proprietaryOptions,
    selectedFilter,
    onFilterSelect,
    openSeaNodeRef: openSeaAutocompleteRef,
    proprietaryNodeRef: proprietaryAutocompleteRef,
    savedFiltersVisible,
  });

  const hasChipError = !openSeaActive && !proprietaryChannelsActive;

  const renderCustomError = () => (
    <div className="filter-header__custom-error">
      <BdsIcon name="error" size="x-small" />
      <span>{t('globalFilter.selectOnlyOneSavedFilter', 'Selecione apenas um filtro salvo')}</span>
    </div>
  );

  if (editMode) {
    return (
      <div className="filter-header">
        <div className="filter-header__top-row">
          <div className="filter-header__icon">
            <BdsIcon name="filter" />
          </div>
          <div className="filter-header__text-content">
            <div className="filter-header__title-wrapper">
              <h2>{t('globalFilter.editSavedFilterTitle', 'Edição de filtro salvo')}</h2>
              <CustomTooltip
                content={t('globalFilter.editSavedFilterTooltip', 'Altere os critérios do filtro selecionado')}
                position="bottom"
                maxWidth="260px"
              >
                <BdsIcon name="info" size="x-small" />
              </CustomTooltip>
            </div>
            <p className="filter-header__subtitle">
              {t('globalFilter.editSavedFilterText')}
            </p>
          </div>
          <button type="button" className="close-btn" aria-label={t('globalFilter.closeLabel')} onClick={onClose}>
            <BdsIcon name="close" />
          </button>
        </div>

        <button type="button" className="filter-header__back-btn" onClick={onBack}>
          <BdsIcon name="arrow-left" size="x-small" />
          <span>{editingFilterName}</span>
        </button>

        {!hideChipsRow && (
          <ChipsSection
            openSeaActive={openSeaActive}
            proprietaryChannelsActive={proprietaryChannelsActive}
            hasChipError={hasChipError}
            onChipChange={onChipChange}
            chipsAreLocked={chipsAreLocked}
            proprietaryChipDisabledReason={proprietaryChipDisabledReason}
          />
        )}
      </div>
    );
  }

  return (
    <div className="filter-header">
      <div className="filter-header__top-row">
        <div className="filter-header__icon">
          <BdsIcon name="filter" />
        </div>

        <div className="filter-header__text-content">
          <div className="filter-header__title-wrapper">
            <h2>{t('globalFilter.title')}</h2>
            <CustomTooltip content={t('globalFilter.infoTooltip')} position="bottom" maxWidth="260px">
              <BdsIcon name="info" size="x-small" />
            </CustomTooltip>
          </div>
          <p className="filter-header__subtitle">{t('globalFilter.subtitle')}</p>
        </div>

        <button type="button" className="close-btn" aria-label={t('globalFilter.closeLabel')} onClick={onClose}>
          <BdsIcon name="close" />
        </button>
      </div>

      {!hideSavedFilters && (
        <div className="filter-header__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'newSearch'}
            className={activeTab === 'newSearch' ? 'active' : ''}
            onClick={() => onTabChange('newSearch')}
          >
            {t('globalFilter.newSearch')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'savedFilters'}
            className={activeTab === 'savedFilters' ? 'active' : ''}
            onClick={() => onTabChange('savedFilters')}
          >
            {t('globalFilter.savedFilters')}
          </button>
        </div>
      )}

      {(hideSavedFilters || activeTab === 'newSearch') && !hideChipsRow && (
        <ChipsSection
          openSeaActive={openSeaActive}
          proprietaryChannelsActive={proprietaryChannelsActive}
          hasChipError={hasChipError}
          onChipChange={onChipChange}
          chipsAreLocked={chipsAreLocked}
          proprietaryChipDisabledReason={proprietaryChipDisabledReason}
        />
      )}

      {!hideSavedFilters && activeTab === 'savedFilters' && (
        <div className="filter-header__saved-filters">
          <p className="filter-header__saved-label">
            {t('globalFilter.selectFilterTitle')}
          </p>

          <p className="filter-header__saved-group-label">
            {t('globalFilter.openSeaSavedFiltersLabel')}
          </p>
          <BdsAutocomplete
            ref={openSeaAutocompleteRef}
            label={t('globalFilter.searchFilterLabel')}
            placeholder={t('globalFilter.searchFilterPlaceholder',)}
            options={openSeaOptions}
            value={openSeaValue}
            loading={isLoadingFilters}
            danger={openSeaHasError}
          />
          {openSeaHasError && renderCustomError()}

          <p className="filter-header__saved-group-label filter-header__saved-group-label--spaced">
            {t('globalFilter.proprietarySavedFiltersLabel')}
          </p>
          <BdsAutocomplete
            ref={proprietaryAutocompleteRef}
            label={t('globalFilter.searchFilterLabel')}
            placeholder={t('globalFilter.searchFilterPlaceholder')}
            options={proprietaryOptions}
            value={proprietaryValue}
            loading={isLoadingFilters}
            danger={proprietaryHasError}
          />
          {proprietaryHasError && renderCustomError()}
        </div>
      )}
    </div>
  );
};
